import React, { useState, useEffect } from 'react';
import { Layout, Menu, Button, Avatar, Dropdown, Badge, Drawer, Tooltip, Modal, Form, Input, Descriptions, Tag } from 'antd';
import {
  DashboardOutlined,
  TeamOutlined,
  UserOutlined,
  BookOutlined,
  FileTextOutlined,
  SettingOutlined,
  LogoutOutlined,
  BellOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
  MenuOutlined,
  SafetyOutlined,
  AuditOutlined,
  BarChartOutlined,
  HomeOutlined,
  LockOutlined,
} from '@ant-design/icons';
import { useNavigate, useLocation, Outlet } from 'react-router-dom';
import { logout, changePassword, getCurrentUser } from '../services/auth';
import { getPublicConfigValue } from '../services/config';
import { useBreakpoint } from '../hooks/useBreakpoint';
import { antdMessage } from '../utils/antdGlobal';
import './AdminLayout.css';

const { Header, Sider, Content } = Layout;

interface AdminLayoutProps {
  userRole: 'super_admin' | 'campus_admin' | 'uploader';
}

const AdminLayout: React.FC<AdminLayoutProps> = ({ userRole }) => {
  const [collapsed, setCollapsed] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [userInfo, setUserInfo] = useState<any>(null);
  const [systemTitle, setSystemTitle] = useState('成绩查询系统');
  const [profileVisible, setProfileVisible] = useState(false);
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [passwordLoading, setPasswordLoading] = useState(false);
  const [fullUserInfo, setFullUserInfo] = useState<any>(null);
  const [passwordForm] = Form.useForm();
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isTablet } = useBreakpoint();

  // 从 localStorage 获取真实用户角色
  const getRealUserRole = (): 'super_admin' | 'campus_admin' | 'uploader' => {
    const storedUserInfo = localStorage.getItem('userInfo');
    if (storedUserInfo) {
      const parsed = JSON.parse(storedUserInfo);
      if (parsed.role === 'super_admin' || parsed.role === 'campus_admin' || parsed.role === 'uploader') {
        return parsed.role;
      }
    }
    return userRole;
  };

  const realUserRole = getRealUserRole();

  useEffect(() => {
    const storedUserInfo = localStorage.getItem('userInfo');
    const storedCampusInfo = localStorage.getItem('campusInfo');
    if (storedUserInfo) {
      setUserInfo({
        ...JSON.parse(storedUserInfo),
        campusName: storedCampusInfo ? JSON.parse(storedCampusInfo).campusName : undefined,
      });
    }
  }, []);

  // 获取系统标题配置
  useEffect(() => {
    const fetchSystemTitle = async () => {
      try {
        const result = await getPublicConfigValue('system_title');
        if (result.value) {
          setSystemTitle(result.value);
          document.title = result.value;
        }
      } catch (error) {
        // 配置不存在时使用默认值
        console.log('Using default system title');
      }
    };
    fetchSystemTitle();
  }, []);

  // 超级管理员菜单
  const superAdminMenuItems = [
    {
      key: '/admin/dashboard',
      icon: <DashboardOutlined />,
      label: '控制台',
    },
    {
      key: '/admin/campuses',
      icon: <HomeOutlined />,
      label: '校区管理',
    },
    {
      key: '/admin/admins',
      icon: <SafetyOutlined />,
      label: '账号管理',
    },
    {
      key: '/admin/students',
      icon: <TeamOutlined />,
      label: '学生管理',
    },
    {
      key: '/admin/grades',
      icon: <FileTextOutlined />,
      label: '成绩管理',
    },
    {
      key: '/admin/statistics',
      icon: <BarChartOutlined />,
      label: '成绩统计',
    },
    {
      key: '/admin/logs',
      icon: <AuditOutlined />,
      label: '操作日志',
    },
    {
      key: '/admin/settings',
      icon: <SettingOutlined />,
      label: '系统配置',
    },
  ];

  // 校区负责人菜单
  const campusAdminMenuItems = [
    {
      key: '/campus/dashboard',
      icon: <DashboardOutlined />,
      label: '控制台',
    },
    {
      key: '/campus/accounts',
      icon: <SafetyOutlined />,
      label: '账号管理',
    },
    {
      key: '/campus/students',
      icon: <TeamOutlined />,
      label: '学生管理',
    },
    {
      key: '/campus/grades',
      icon: <FileTextOutlined />,
      label: '成绩管理',
    },
    {
      key: '/campus/grade-review',
      icon: <AuditOutlined />,
      label: '成绩审核',
    },
    {
      key: '/campus/statistics',
      icon: <BarChartOutlined />,
      label: '成绩统计',
    },
  ];

  // 上传专员菜单
  const uploaderMenuItems = [
    {
      key: '/campus/dashboard',
      icon: <DashboardOutlined />,
      label: '控制台',
    },
    {
      key: '/campus/students',
      icon: <TeamOutlined />,
      label: '学生管理',
    },
    {
      key: '/campus/grades',
      icon: <FileTextOutlined />,
      label: '成绩管理',
    },
  ];

  const menuItems = realUserRole === 'super_admin' 
    ? superAdminMenuItems 
    : realUserRole === 'uploader' 
      ? uploaderMenuItems 
      : campusAdminMenuItems;

  const handleMenuClick = (key: string) => {
    navigate(key);
    setDrawerOpen(false);
  };

  const handleLogout = () => {
    logout();
  };

  const userMenuItems = [
    {
      key: 'profile',
      icon: <UserOutlined />,
      label: '个人信息',
    },
    {
      key: 'password',
      icon: <SettingOutlined />,
      label: '修改密码',
    },
    {
      type: 'divider' as const,
    },
    {
      key: 'logout',
      icon: <LogoutOutlined />,
      label: '退出登录',
      danger: true,
    },
  ];

  const handleUserMenuClick = ({ key }: { key: string }) => {
    switch (key) {
      case 'profile':
        getCurrentUser().then(info => setFullUserInfo(info)).catch(() => {});
        setProfileVisible(true);
        break;
      case 'password':
        passwordForm.resetFields();
        setPasswordVisible(true);
        break;
      case 'logout':
        handleLogout();
        break;
    }
  };

  const handleChangePassword = async (values: any) => {
    setPasswordLoading(true);
    try {
      await changePassword(values.oldPassword, values.newPassword);
      antdMessage.success('密码修改成功，请重新登录');
      setPasswordVisible(false);
      setTimeout(() => logout(), 1500);
    } catch {
      // 错误已由请求拦截器统一提示，此处无需重复显示
    } finally {
      setPasswordLoading(false);
    }
  };

  // Logo 区域
  const LogoSection = () => (
    <div className="admin-layout-logo">
      <div className="admin-layout-logo-icon">
        <BookOutlined />
      </div>
      {(!collapsed || isMobile) && (
        <div className="admin-layout-logo-text">
          <span className="admin-layout-logo-title">{systemTitle}</span>
          <span className="admin-layout-logo-subtitle">Grade Query</span>
        </div>
      )}
    </div>
  );

  // 菜单组件
  const menuContent = (
    <>
      <LogoSection />
      <Menu
        mode="inline"
        selectedKeys={[location.pathname]}
        items={menuItems}
        onClick={({ key }) => handleMenuClick(key)}
        className="admin-layout-menu"
      />
    </>
  );

  return (
    <>
    <Layout className="admin-layout">
      {/* 桌面端侧边栏 */}
      {!isMobile && (
        <Sider
          trigger={null}
          collapsible
          collapsed={collapsed}
          theme="light"
          className="admin-layout-sider"
          width={240}
        >
          {menuContent}
        </Sider>
      )}

      {/* 移动端抽屉菜单 */}
      <Drawer
        title={null}
        placement="left"
        open={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        size="default"
        className="admin-layout-drawer"
        closable={false}
        styles={{ wrapper: { width: 260 } }}
      >
        {menuContent}
      </Drawer>

      <Layout className="admin-layout-main">
        <Header className="admin-layout-header">
          <div className="admin-layout-header-left">
            {isMobile ? (
              <Button
                type="text"
                icon={<MenuOutlined />}
                onClick={() => setDrawerOpen(true)}
                className="admin-layout-menu-btn"
              />
            ) : (
              <Tooltip title={collapsed ? '展开菜单' : '收起菜单'}>
                <Button
                  type="text"
                  icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
                  onClick={() => setCollapsed(!collapsed)}
                  className="admin-layout-menu-btn"
                />
              </Tooltip>
            )}
            <span className="admin-layout-breadcrumb">
              {menuItems.find(item => item.key === location.pathname)?.label || '控制台'}
            </span>
          </div>
          
          <div className="admin-layout-header-right">
            {!isMobile && (
              <Tooltip title="通知">
                <Badge count={0} size="small">
                  <Button 
                    type="text" 
                    icon={<BellOutlined />} 
                    className="admin-layout-header-btn"
                  />
                </Badge>
              </Tooltip>
            )}
            
            <Dropdown
              menu={{ items: userMenuItems, onClick: handleUserMenuClick }}
              placement="bottomRight"
              arrow
            >
              <div className="admin-layout-user">
                <Avatar 
                  icon={<UserOutlined />} 
                  className="admin-layout-user-avatar"
                />
                {!isMobile && (
                  <div className="admin-layout-user-info">
                    <div className="admin-layout-user-name">
                      {userInfo?.account || '管理员'}
                    </div>
                    <div className="admin-layout-user-role">
                      {userRole === 'super_admin' ? '超级管理员' : userRole === 'uploader' ? '上传专员' : userInfo?.campusName || '校区负责人'}
                    </div>
                  </div>
                )}
              </div>
            </Dropdown>
          </div>
        </Header>
        
        <Content className="admin-layout-content">
          <div className="admin-layout-content-inner">
            <Outlet />
          </div>
        </Content>
      </Layout>
    </Layout>

      {/* 个人信息 Modal */}
      <Modal
        title="个人信息"
        open={profileVisible}
        onCancel={() => setProfileVisible(false)}
        footer={null}
        width={460}
      >
        <Descriptions column={1} bordered size="small" style={{ marginTop: 8 }}>
          <Descriptions.Item label="账号">
            {fullUserInfo?.account || userInfo?.account || '-'}
          </Descriptions.Item>
          <Descriptions.Item label="角色">
            <Tag color={fullUserInfo?.role === 'super_admin' ? 'purple' : 'blue'}>
              {fullUserInfo?.role === 'super_admin' ? '超级管理员' : fullUserInfo?.role === 'uploader' ? '上传专员' : '校区负责人'}
            </Tag>
          </Descriptions.Item>
          {fullUserInfo?.campusInfo && (
            <Descriptions.Item label="所属校区">
              {fullUserInfo.campusInfo.campus_name || fullUserInfo.campusInfo.campusName}
            </Descriptions.Item>
          )}
          <Descriptions.Item label="账号状态">
            <Tag color={fullUserInfo?.status === 'enabled' ? 'success' : 'error'}>
              {fullUserInfo?.status === 'enabled' ? '正常' : '已禁用'}
            </Tag>
          </Descriptions.Item>
          <Descriptions.Item label="上次登录">
            {fullUserInfo?.lastLoginAt
              ? new Date(fullUserInfo.lastLoginAt).toLocaleString('zh-CN')
              : '-'}
          </Descriptions.Item>
        </Descriptions>
      </Modal>

      {/* 修改密码 Modal */}
      <Modal
        title="修改密码"
        open={passwordVisible}
        onCancel={() => { setPasswordVisible(false); passwordForm.resetFields(); }}
        footer={null}
        width={420}
        destroyOnHidden
      >
        <Form
          form={passwordForm}
          layout="vertical"
          onFinish={handleChangePassword}
          style={{ marginTop: 8 }}
        >
          <Form.Item
            name="oldPassword"
            label="原密码"
            rules={[{ required: true, message: '请输入原密码' }]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请输入原密码" />
          </Form.Item>

          <Form.Item
            name="newPassword"
            label="新密码"
            rules={[
              { required: true, message: '请输入新密码' },
              { min: 8, message: '密码至少8位' },
              {
                pattern: /^(?=.*[a-zA-Z])(?=.*\d)(?=.*[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]).*$/,
                message: '密码必须包含字母、数字和特殊符号',
              },
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="至少8位，含字母、数字和特殊符号" />
          </Form.Item>

          <Form.Item
            name="confirmPassword"
            label="确认新密码"
            dependencies={['newPassword']}
            rules={[
              { required: true, message: '请确认新密码' },
              ({ getFieldValue }) => ({
                validator(_, value) {
                  if (!value || getFieldValue('newPassword') === value) {
                    return Promise.resolve();
                  }
                  return Promise.reject(new Error('两次输入的密码不一致'));
                },
              }),
            ]}
          >
            <Input.Password prefix={<LockOutlined />} placeholder="请再次输入新密码" />
          </Form.Item>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              type="primary"
              htmlType="submit"
              block
              loading={passwordLoading}
            >
              确认修改
            </Button>
          </Form.Item>
        </Form>
      </Modal>
    </>
  );
};

export default AdminLayout;
